import { useEffect, useMemo, useState } from 'react';

import GroupedAmountInput from 'components/GroupedAmountInput';

import {
  Box,
  Button,
  Checkbox,
  Flex,
  Input,
  Link,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  NumberInput,
  NumberInputField,
  Table,
  Textarea,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useToast,
} from '@chakra-ui/react';
import PropTypes from 'prop-types';
import MarketingSpendSection from 'pages/Cockpit/Customer/Performance/Marketing/MarketingSpendSection';
import { useUserName } from './useUserName';
import { Link as RouterLink } from 'react-router-dom';

import {
  consecutiveRoasText,
  customersMissingRepaymentCurve,
  defaultMonthlyIrr,
  extendFundingEnabled,
  fillGrowthPercent,
  formatGroupedNumber,
  hasDefaultMarketing,
  hasMarketingFromMonth,
  liveMarketingGrowthPercent,
  formatInstallmentDate,
  formatYearMonth,
  marketingFill,
  marketingFillSummary,
  ownLiveMarketingGrowth,
  parseConsecutiveRoas,
  parseGroupedNumber,
  pipelineRecalcPayload,
  shownPipelineClient,
  utcYearMonth,
} from './pipelineExposure';

const headerCell = {
  fontSize: '10px',
  letterSpacing: '0.04em',
  color: 'gray.500',
  textTransform: 'uppercase',
  whiteSpace: 'nowrap',
  py: 2,
  px: 2,
};

function DraftNumber({
  value,
  onCommit,
  width = '88px',
  precision = 0,
  grouped = false,
  ariaLabel,
  placeholder,
}) {
  const [draft, setDraft] = useState(value ?? '');
  useEffect(() => {
    setDraft(grouped ? parseGroupedNumber(value ?? '') : (value ?? ''));
  }, [value, grouped]);
  if (grouped) {
    return (
      <GroupedAmountInput
        width={width}
        value={draft}
        aria-label={ariaLabel}
        onChange={setDraft}
        onBlur={() => onCommit(parseGroupedNumber(draft))}
      />
    );
  }
  return (
    <NumberInput
      size="sm"
      w={width}
      value={draft}
      precision={precision}
      onChange={(next) => setDraft(next)}
      onBlur={() => onCommit(draft)}
    >
      <NumberInputField px={2} aria-label={ariaLabel} placeholder={placeholder} />
    </NumberInput>
  );
}

DraftNumber.propTypes = {
  placeholder: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onCommit: PropTypes.func.isRequired,
  width: PropTypes.string,
  precision: PropTypes.number,
  grouped: PropTypes.bool,
  ariaLabel: PropTypes.string,
};

function CurvePasteField({ value, onChange }) {
  const parsed = parseConsecutiveRoas(value);
  return (
    <Box>
      <Text fontSize="sm" color="gray.600" mb={2}>
        Paste cumulative ROAS percents starting at month 0. Separate numbers with commas or tabs. The first number is month 0, the next is month 1, and so on.
      </Text>
      <Textarea
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder="10, 25, 40, 80"
        rows={4}
      />
      {parsed.error ? (
        <Text fontSize="sm" color="red.500" mt={2}>{parsed.error}</Text>
      ) : parsed.points.length > 0 && (
        <Text fontSize="xs" color="gray.500" mt={2}>
          {parsed.points.map((point) => `Month ${point.month}: ${point.cumulativeRoasPct}%`).join(' · ')}
        </Text>
      )}
    </Box>
  );
}

CurvePasteField.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
};

function percentDraft(fraction) {
  if (fraction === '' || fraction === null || fraction === undefined) return '';
  return Math.round(Number(fraction) * 1000) / 10;
}

function feeDraft(fraction) {
  const number = Number(fraction);
  if (!Number.isFinite(number) || number === 0) return 1.4;
  return percentDraft(number);
}

function nextUtcMonth() {
  const now = new Date();
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1)).toISOString().slice(0, 7);
}

function newFutureCustomerId() {
  const random = window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  return `future-${random}`;
}

function columnCount(showCaps, future) {
  // Marketing is on every row; Delete only on future rows.
  return 11 + 1 + (future ? 1 : 0) + 1 + (showCaps ? 2 : 0);
}

function Hint({ label }) {
  return (
    <Tooltip label={label} hasArrow>
      <Box as="span" ml={1} color="gray.500" fontWeight={700} cursor="help">?</Box>
    </Tooltip>
  );
}

Hint.propTypes = {
  label: PropTypes.string.isRequired,
};

const lagHint = 'Months after the cohort when that cohort is funded. Lag 1 funds an October cohort in November. Lag 2 funds it in December. The first repayment is the same number of months after the money goes out.';
const extendFundingHint = 'Funds every customer through the Through month. Months past the saved marketing are filled as explained in How marketing is filled in. Press Save & Run after you change the Through month.';
const lastInstallmentHint = 'When the commitment ends, counted on the latest deal: its first installment plus the commitment length (a 20-month commitment starting Sep 2026 ends Apr 2028). Installments on earlier deals do not count. A deal with no installment yet is counted from its commitment date (the effective date) plus the commitment length. Marketing & Funding projects a funding every month up to this month.';
const liveMarketingGrowthHint = 'After the last saved marketing month, each later month is this much higher. 10% turns 1,000,000 into 1,100,000, then 1,210,000. Leave it empty to use the default marketing growth.';

function monthsText(span, next = false) {
  if (!span) return '';
  const from = formatYearMonth(span.from);
  const label = span.count === 1 ? `1 month (${from})` : `${span.count} months (${from} – ${formatYearMonth(span.through)})`;
  return next ? `The next ${label}` : label;
}

function defaultMarketingText(settings) {
  const spend = Number(settings?.defaultMarketingSpend) || 0;
  return `$${formatGroupedNumber(spend)} in the first cohort month, growing ${fillGrowthPercent(settings)}% a month`;
}

function MarketingFillExplanation({ settings }) {
  const growth = fillGrowthPercent(settings);
  const hasDefault = hasDefaultMarketing(settings);
  return (
    <Box bg="gray.50" borderRadius="8px" px={4} py={3} mb={4} fontSize="sm" color="gray.700">
      <Text fontWeight={700} mb={1}>How marketing is filled in</Text>
      <Text mb={1}>The same rule applies to live, non-live, and future customers, for every month that is funded:</Text>
      <Box as="ul" pl={5}>
        <Box as="li">Saved marketing is always used as entered.</Box>
        <Box as="li">
          A month with no saved marketing, after a saved month (in the funding window, or the month just before it),
          grows from the last saved month by the growth % each month. Gaps between saved months are filled the same way.
        </Box>
        <Box as="li">
          {hasDefault
            ? `A month with no saved marketing before it (or only marketing from long before funding starts) uses the default marketing: ${defaultMarketingText(settings)}. Old marketing is never grown across a gap.`
            : 'A month with no saved marketing before it (or only marketing from long before funding starts) is not funded until you set Default marketing. Old marketing is never grown across a gap.'}
        </Box>
        <Box as="li">
          {`Growth %: a live customer uses its own Marketing growth % when it is filled in. Everyone else uses ${growth}%, `}
          {hasDefault ? 'the monthly growth in Default marketing.' : 'until Default marketing is set; after that, its monthly growth.'}
        </Box>
      </Box>
      <Text mt={1} color="gray.600">
        Orange notes on the rows, and coloured cells on Marketing &amp; Funding, show the months that use these assumptions. Enter real marketing to replace them.
      </Text>
    </Box>
  );
}

MarketingFillExplanation.propTypes = {
  settings: PropTypes.object,
};

function CapsToggle({ showCaps, onToggle }) {
  return (
    <Th {...headerCell}>
      <Button
        size="xs"
        variant="ghost"
        aria-label={showCaps ? 'Hide max cap and max exposure' : 'Show max cap and max exposure'}
        onClick={onToggle}
      >
        {showCaps ? '−' : '+'}
      </Button>
    </Th>
  );
}

CapsToggle.propTypes = {
  showCaps: PropTypes.bool,
  onToggle: PropTypes.func.isRequired,
};

function FeeHeader() {
  return (
    <Th {...headerCell}>
      <Tooltip label="monthly IRR" hasArrow>
        <Box as="span">Fee</Box>
      </Tooltip>
    </Th>
  );
}

function FirstCohortFundedHeader() {
  return (
    <Th {...headerCell}>
      <Tooltip label="The first cohort we fund. An October cohort with lag 1 is funded in November. With lag 2 it is funded in December." hasArrow>
        <Box as="span">First cohort funded</Box>
      </Tooltip>
    </Th>
  );
}

const NO_ROWS = [];

const NON_LIVE_OPEN_KEY = 'sourcesUsesNonLiveOpen';

function readNonLiveOpen() {
  try {
    return window.localStorage.getItem(NON_LIVE_OPEN_KEY) === 'true';
  } catch {
    return false;
  }
}

function storeNonLiveOpen(open) {
  try {
    window.localStorage.setItem(NON_LIVE_OPEN_KEY, String(open));
  } catch {
    // Storage can be blocked; the section still opens and closes.
  }
}

function formatLastRun(settings, userName) {
  if (!settings?.lastRunAt) return '';
  const date = new Date(settings.lastRunAt);
  if (Number.isNaN(date.getTime())) return '';
  const when = date.toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
  });
  const who = settings.lastRunBy ? userName(settings.lastRunBy) : '';
  return who ? `Last run by ${who} on ${when}` : `Last run on ${when}`;
}

function clientStage(client) {
  if (client.future) return 'Future';
  return String(client.stage || '').trim() || 'Unspecified';
}

function PipelineClientsTable({
  clients,
  liveCustomers,
  settings,
  tenant,
  fundingEndMonth,
  startMonth = '',
  endMonth = '',
  onStartMonthChange,
  onEndMonthChange,
  showToolbar = true,
  stages = null,
  onRecalculate,
  recalculating,
  readOnly = false,
  onDirtyChange,
  runRef,
}) {
  const toast = useToast();
  const userName = useUserName();
  // The non-live table starts closed; its warnings stay visible either way.
  const [nonLiveOpen, setNonLiveOpen] = useState(readNonLiveOpen);
  const baseRows = clients || NO_ROWS;
  const baseLiveRows = liveCustomers || NO_ROWS;
  const [rows, setRows] = useState(baseRows);
  const [liveRows, setLiveRows] = useState(baseLiveRows);
  const [localSettings, setLocalSettings] = useState(settings);
  const [showCaps, setShowCaps] = useState(false);
  const [vectorClientId, setVectorClientId] = useState(null);
  const [vectorText, setVectorText] = useState('');
  const [genericOpen, setGenericOpen] = useState(false);
  const [genericText, setGenericText] = useState('');
  const [marketingClientId, setMarketingClientId] = useState(null);
  const [marketingRows, setMarketingRows] = useState([]);
  // Non-live customer whose real marketing (the marketing page's manual spend) is open in a popup.
  const [realMarketingClient, setRealMarketingClient] = useState(null);
  const [defaultMarketingOpen, setDefaultMarketingOpen] = useState(false);
  const [defaultSpendDraft, setDefaultSpendDraft] = useState('');
  const [defaultGrowthDraft, setDefaultGrowthDraft] = useState('');

  useEffect(() => {
    setRows(baseRows);
  }, [baseRows]);

  useEffect(() => {
    setLiveRows(baseLiveRows);
  }, [baseLiveRows]);

  useEffect(() => {
    setLocalSettings(settings);
  }, [settings]);

  // Any edit replaces one of these, so a difference from what was loaded is unsaved.
  const dirty = rows !== baseRows || liveRows !== baseLiveRows || localSettings !== settings;
  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);

  const vectorClient = useMemo(
    () => rows.find((client) => client.customerId === vectorClientId) || null,
    [rows, vectorClientId]
  );
  const marketingClient = useMemo(
    () => rows.find((client) => client.customerId === marketingClientId) || null,
    [rows, marketingClientId]
  );
  const stageMatches = (client) => stages == null || stages.includes(clientStage(client));
  const customers = rows.filter((client) => !client.future && shownPipelineClient(client, tenant) && stageMatches(client));
  const futureCustomers = rows.filter((client) => client.future && shownPipelineClient(client, tenant) && stageMatches(client));

  const patchRow = (customerId, patch) => {
    setRows((current) => current.map((row) => (
      row.customerId === customerId ? { ...row, ...patch } : row
    )));
  };

  const patchLiveRow = (customerId, patch) => {
    setLiveRows((current) => current.map((row) => (
      row.customerId === customerId ? { ...row, ...patch } : row
    )));
  };

  const openGeneric = () => {
    setGenericText(consecutiveRoasText(localSettings?.genericVector));
    setGenericOpen(true);
  };

  const commitGeneric = () => {
    const parsed = parseConsecutiveRoas(genericText);
    if (parsed.error) {
      toast({ title: parsed.error, status: 'error', duration: 4000 });
      return;
    }
    setLocalSettings((current) => ({ ...(current || {}), genericVector: parsed.points }));
    setRows((current) => current.map((row) => (
      row.useGenericCurve ? { ...row, missingGenericCurve: parsed.points.length === 0 } : row
    )));
    setGenericOpen(false);
  };

  const openVector = (client) => {
    setVectorClientId(client.customerId);
    setVectorText(consecutiveRoasText(client.repaymentVector));
  };

  const commitVector = () => {
    if (!vectorClient) return;
    const parsed = parseConsecutiveRoas(vectorText);
    if (parsed.error) {
      toast({ title: parsed.error, status: 'error', duration: 4000 });
      return;
    }
    patchRow(vectorClient.customerId, {
      repaymentVector: parsed.points,
      missingRepaymentVector: parsed.points.length === 0,
    });
    setVectorClientId(null);
  };

  const openMarketing = (client) => {
    const current = (client.marketing || []).map((row) => ({ month: row.month || '', spend: row.spend || 0 }));
    setMarketingClientId(client.customerId);
    setMarketingRows(current.length > 0 ? current : [{ month: nextUtcMonth(), spend: 0 }]);
  };

  const commitMarketing = () => {
    if (!marketingClient) return;
    const kept = marketingRows
      .map((row) => ({
        month: row.month,
        spend: Number(parseGroupedNumber(row.spend)) || 0,
      }))
      .filter((row) => row.spend > 0);
    const seen = new Set();
    for (const row of kept) {
      if (!/^\d{4}-\d{2}$/.test(row.month || '')) {
        toast({ title: 'Enter a month for each marketing amount', status: 'error', duration: 4000 });
        return;
      }
      if (seen.has(row.month)) {
        toast({ title: 'Marketing has a duplicate month', status: 'error', duration: 4000 });
        return;
      }
      seen.add(row.month);
    }
    patchRow(marketingClient.customerId, {
      marketing: kept,
      missingFutureMarketing: !hasMarketingFromMonth(kept, utcYearMonth()),
    });
    setMarketingClientId(null);
  };

  const addFutureCustomer = () => {
    setRows((current) => [...current, {
      customerId: newFutureCustomerId(),
      companyName: '',
      stage: 'Future',
      future: true,
      tenant: tenant || '',
      included: false,
      hidden: false,
      firstFundingMonth: nextUtcMonth(),
      fundingPct: 0.8,
      monthlyIrr: defaultMonthlyIrr,
      cohorts: 24,
      lagMonths: 1,
      maxCap: null,
      maxExposure: null,
      repaymentVector: [],
      marketing: [],
      missingFutureMarketing: true,
      missingRepaymentVector: true,
    }]);
  };

  const openDefaultMarketing = () => {
    const growth = Number(localSettings?.defaultMarketingGrowth);
    setDefaultSpendDraft(localSettings?.defaultMarketingSpend || '');
    setDefaultGrowthDraft(Number.isFinite(growth) ? percentDraft(growth) : '');
    setDefaultMarketingOpen(true);
  };

  const commitDefaultMarketing = () => {
    const spend = Number(parseGroupedNumber(defaultSpendDraft)) || 0;
    if (!Number.isFinite(spend) || spend < 0) {
      toast({ title: 'Enter a first marketing spend', status: 'error', duration: 4000 });
      return;
    }
    const growthText = String(defaultGrowthDraft ?? '').trim();
    const growthPct = growthText === '' ? 0 : Number(growthText);
    if (!Number.isFinite(growthPct) || growthPct < 0 || growthPct > 100) {
      toast({ title: 'Monthly growth must be between 0 and 100', status: 'error', duration: 4000 });
      return;
    }
    setLocalSettings((current) => ({
      ...(current || {}),
      defaultMarketingSpend: spend,
      defaultMarketingGrowth: growthPct / 100,
    }));
    setDefaultMarketingOpen(false);
  };

  const setAllIncluded = (sectionRows, included) => {
    const ids = new Set(sectionRows.map((client) => client.customerId));
    setRows((current) => current.map((row) => (
      ids.has(row.customerId) ? { ...row, included } : row
    )));
  };

  const setAllGeneric = (sectionRows, useGenericCurve) => {
    const ids = new Set(sectionRows.map((client) => client.customerId));
    const missingGenericCurve = useGenericCurve && (localSettings?.genericVector || []).length === 0;
    setRows((current) => current.map((row) => (
      ids.has(row.customerId) ? { ...row, useGenericCurve, missingGenericCurve } : row
    )));
  };

  const recalculate = () => {
    const futureRows = rows.filter((client) => client.future && shownPipelineClient(client, tenant));
    if (futureRows.some((client) => !String(client.companyName || '').trim())) {
      toast({ title: 'Enter a name for each future customer', status: 'error', duration: 4000 });
      return false;
    }
    const missingCurve = customersMissingRepaymentCurve(rows, localSettings);
    if (missingCurve.length > 0) {
      const names = missingCurve.map((client) => client.companyName || 'Unnamed future customer').join(', ');
      const genericOnly = missingCurve.every((client) => client.useGenericCurve);
      toast({
        title: 'Save & Run did not start',
        description: genericOnly
          ? `${names} ${missingCurve.length === 1 ? 'uses' : 'use'} the generic curve, but it is empty. Enter it with Generic curve, then press Save & Run again.`
          : `${names} ${missingCurve.length === 1 ? 'has' : 'have'} no repayment vector. Enter one with Repayment vector, or check Generic, then press Save & Run again.`,
        status: 'error',
        duration: 10000,
        isClosable: true,
      });
      return false;
    }
    onRecalculate(pipelineRecalcPayload([
      ...rows,
      ...liveRows.map((row) => ({
        ...row,
        included: false,
        future: false,
        hidden: false,
        marketingGrowth: ownLiveMarketingGrowth(row.marketingGrowth),
      })),
    ], {
      ...localSettings,
      lastFundingMonth: fundingEndMonth || '',
      extendFunding: extendFundingEnabled(localSettings),
    }, tenant));
    return true;
  };

  // The page can start Save & Run itself (Save and leave). It returns false when
  // a check stopped it.
  if (runRef) runRef.current = recalculate;

  const renderRow = (client, future) => {
    const name = client.companyName || 'future customer';
    const fill = marketingFill(client, localSettings, fundingEndMonth);
    const unfunded = !!fill?.noDefault;
    const replaceAction = future ? (
      <Link as="button" type="button" color="brand.500" textDecoration="underline" onClick={() => openMarketing(client)}>
        Enter marketing
      </Link>
    ) : (
      <Link as="button" type="button" color="brand.500" textDecoration="underline" onClick={() => setRealMarketingClient(client)}>
        Update marketing
      </Link>
    );
    return (
      <Tr key={client.customerId}>
        <Td px={2} minW="160px">
          {future ? (
            <Input
              size="sm"
              value={client.companyName || ''}
              placeholder="Name"
              aria-label="Future customer name"
              color={unfunded ? 'red.600' : undefined}
              onChange={(event) => patchRow(client.customerId, { companyName: event.target.value })}
            />
          ) : (
            <Text fontSize="sm" fontWeight={600} color={unfunded ? 'red.600' : undefined}>{client.companyName}</Text>
          )}
          {fill && (
            <Box fontSize="xs" mt={1} maxW="340px">
              {fill.defaults && !fill.noDefault && (
                <Text color="orange.600">
                  {`No saved marketing for ${monthsText(fill.defaults)}. Using default marketing: ${defaultMarketingText(localSettings)}.`}
                </Text>
              )}
              {fill.noDefault && (
                <Text color="red.600">
                  {`No marketing for ${monthsText(fill.defaults)} and no default marketing is set, so those months are not funded. Set Default marketing above.`}
                </Text>
              )}
              {fill.continued && (
                <Text color="orange.600">
                  {`Saved marketing ends ${formatYearMonth(fill.lastSaved)}. ${monthsText(fill.continued, true)} grow ${fillGrowthPercent(localSettings)}% a month from it.`}
                </Text>
              )}
              <Text color="gray.600">
                {replaceAction}
                {' to replace these assumptions with real numbers.'}
              </Text>
            </Box>
          )}
          {!client.missingFutureMarketing && client.useGenericCurve && client.missingGenericCurve && (
            <Text fontSize="xs" color="orange.600" mt={1}>
              Enter a generic curve to calculate returns.
            </Text>
          )}
          {!client.missingFutureMarketing && !client.useGenericCurve && client.missingRepaymentVector && (
            <Text fontSize="xs" color="orange.600" mt={1}>
              Enter a repayment vector to calculate returns.
            </Text>
          )}
        </Td>
        <Td px={2} fontSize="sm" whiteSpace="nowrap">{future ? 'Future' : client.stage}</Td>
        <Td px={2}>
          <Checkbox
            isChecked={!!client.included}
            onChange={(event) => patchRow(client.customerId, { included: event.target.checked })}
            aria-label={`Include ${name}`}
          />
        </Td>
        <Td px={2}>
          <Input
            type="month"
            size="sm"
            w="140px"
            value={client.firstFundingMonth || ''}
            onChange={(event) => patchRow(client.customerId, { firstFundingMonth: event.target.value })}
          />
        </Td>
        <Td px={2}>
          <DraftNumber
            width="72px"
            precision={1}
            value={percentDraft(client.fundingPct)}
            onCommit={(next) => patchRow(client.customerId, { fundingPct: next === '' ? 0 : Number(next) / 100 })}
          />
        </Td>
        <Td px={2}>
          <DraftNumber
            width="72px"
            precision={1}
            ariaLabel={`Fee for ${name}`}
            value={feeDraft(client.monthlyIrr)}
            onCommit={(next) => patchRow(client.customerId, { monthlyIrr: next === '' ? 0 : Number(next) / 100 })}
          />
        </Td>
        <Td px={2}>
          <DraftNumber
            width="64px"
            value={client.cohorts}
            onCommit={(next) => patchRow(client.customerId, { cohorts: Number(next) || 0 })}
          />
        </Td>
        <Td px={2}>
          <DraftNumber
            width="56px"
            value={client.lagMonths}
            onCommit={(next) => patchRow(client.customerId, { lagMonths: Number(next) || 0 })}
          />
        </Td>
        <Td px={2}>
          <Checkbox
            isChecked={!!client.useGenericCurve}
            onChange={(event) => patchRow(client.customerId, {
              useGenericCurve: event.target.checked,
              missingGenericCurve: event.target.checked && (localSettings?.genericVector || []).length === 0,
            })}
            aria-label={`Use generic curve for ${name}`}
          />
        </Td>
        <Td px={2}>
          <Button size="xs" variant="outline" onClick={() => openVector(client)}>
            Repayment vector
          </Button>
        </Td>
        <Td px={2}>
          <Button
            size="xs"
            variant="outline"
            onClick={() => (future ? openMarketing(client) : setRealMarketingClient(client))}
          >
            Marketing
          </Button>
        </Td>
        {future && (
          <Td px={2}>
            <Button
              size="xs"
              variant="outline"
              colorScheme="red"
              aria-label={`Delete ${name}`}
              onClick={() => {
                setRows((current) => current.filter((row) => row.customerId !== client.customerId));
                toast({ title: 'Press Save & Run to delete this customer', status: 'info', duration: 4000 });
              }}
            >
              Delete
            </Button>
          </Td>
        )}
        <Td px={2} />
        {showCaps && (
          <>
            <Td px={2}>
              <DraftNumber
                grouped
                width="128px"
                ariaLabel={`Max cap for ${name}`}
                value={client.maxCap ?? ''}
                onCommit={(next) => patchRow(client.customerId, { maxCap: next === '' ? null : Number(next) })}
              />
            </Td>
            <Td px={2}>
              <DraftNumber
                grouped
                width="128px"
                ariaLabel={`Max exposure for ${name}`}
                value={client.maxExposure ?? ''}
                onCommit={(next) => patchRow(client.customerId, { maxExposure: next === '' ? null : Number(next) })}
              />
            </Td>
          </>
        )}
      </Tr>
    );
  };

  const renderGapSummary = (sectionRows, future) => {
    const text = marketingFillSummary(
      sectionRows.map((client) => marketingFill(client, localSettings, fundingEndMonth)),
      future ? 'future' : 'non-live'
    );
    if (!text) return null;
    return (
      <Text fontSize="sm" color="orange.600" mb={2}>{text}</Text>
    );
  };

  // With the table closed, each red warning stays visible, with the customer's name.
  const renderUnfundedWarnings = (sectionRows) => {
    const unfunded = sectionRows
      .map((client) => ({ client, fill: marketingFill(client, localSettings, fundingEndMonth) }))
      .filter(({ fill }) => fill?.noDefault);
    if (!unfunded.length) return null;
    return (
      <Box mb={4}>
        {unfunded.map(({ client, fill }) => (
          <Text key={client.customerId} fontSize="sm" color="red.600" mb={1}>
            <Text as="span" fontWeight={700}>{client.companyName}</Text>
            {`: no marketing for ${monthsText(fill.defaults)} and no default marketing is set, so those months are not funded. `}
            <Link as="button" type="button" color="red.600" textDecoration="underline" onClick={() => setRealMarketingClient(client)}>
              Update marketing
            </Link>
          </Text>
        ))}
      </Box>
    );
  };

  const renderTable = (sectionRows, future) => {
    const includable = sectionRows;
    const includedCount = includable.filter((client) => client.included).length;
    const genericCount = sectionRows.filter((client) => client.useGenericCurve).length;
    const allIncluded = includable.length > 0 && includedCount === includable.length;
    const allGeneric = sectionRows.length > 0 && genericCount === sectionRows.length;
    return (
    <Box overflowX="auto" borderWidth="1px" borderColor="gray.100" borderRadius="12px">
      <Table size="sm" variant="simple">
        <Thead>
          <Tr>
            <Th {...headerCell}>{future ? 'Name' : 'Customer'}</Th>
            <Th {...headerCell}>Stage</Th>
            <Th {...headerCell}>
              <Flex align="center" gap={1}>
                <Checkbox
                  isChecked={allIncluded}
                  isIndeterminate={includedCount > 0 && !allIncluded}
                  isDisabled={includable.length === 0}
                  aria-label={future ? 'Include all future customers' : 'Include all non-live customers'}
                  onChange={() => setAllIncluded(sectionRows, !allIncluded)}
                />
                Incl.
              </Flex>
            </Th>
            <FirstCohortFundedHeader />
            <Th {...headerCell}>Fund %</Th>
            <FeeHeader />
            <Th {...headerCell}>Cohorts</Th>
            <Th {...headerCell}>
              <Flex align="center">
                Lag
                <Hint label={lagHint} />
              </Flex>
            </Th>
            <Th {...headerCell}>
              <Flex align="center" gap={1}>
                <Checkbox
                  isChecked={allGeneric}
                  isIndeterminate={genericCount > 0 && !allGeneric}
                  isDisabled={sectionRows.length === 0}
                  aria-label={future ? 'Use generic curve for all future customers' : 'Use generic curve for all non-live customers'}
                  onChange={() => setAllGeneric(sectionRows, !allGeneric)}
                />
                Generic
              </Flex>
            </Th>
            <Th {...headerCell}>Repayment vector</Th>
            <Th {...headerCell}>Marketing</Th>
            {future && <Th {...headerCell}>Delete</Th>}
            <CapsToggle showCaps={showCaps} onToggle={() => setShowCaps((open) => !open)} />
            {showCaps && <Th {...headerCell}>Max cap</Th>}
            {showCaps && <Th {...headerCell}>Max exposure</Th>}
          </Tr>
        </Thead>
        <Tbody>
          {sectionRows.length === 0 && (
            <Tr>
              <Td colSpan={columnCount(showCaps, future)}>
                <Text fontSize="sm" color="gray.500" py={4}>
                  {future ? 'No future customers.' : 'No non-live customers in this tenant.'}
                </Text>
              </Td>
            </Tr>
          )}
          {sectionRows.map((client) => renderRow(client, future))}
        </Tbody>
      </Table>
    </Box>
    );
  };

  return (
    // A read-only scenario disables every input and button in the table.
    <Box as="fieldset" disabled={readOnly} mb={6} mx={0} minW={0} border={0} p={0}>
      {showToolbar && (
      <Box mb={4}>
      <Flex align="center" gap={3} mb={3} wrap="wrap">
        <Flex align="center" gap={1}>
          <Tooltip hasArrow label="First month shown.">
            <Text fontSize="xs" color="gray.500" cursor="help">From</Text>
          </Tooltip>
          <Input
            type="month"
            size="sm"
            w="150px"
            aria-label="From"
            value={startMonth || ''}
            onChange={(event) => onStartMonthChange?.(event.target.value)}
          />
        </Flex>
        <Flex align="center" gap={1}>
          <Tooltip hasArrow label="Last month shown. Funding stays as it is until you check Extend funding and press Save & Run.">
            <Text fontSize="xs" color="gray.500" cursor="help">Through</Text>
          </Tooltip>
          <Input
            type="month"
            size="sm"
            w="150px"
            aria-label="Through"
            value={endMonth || ''}
            onChange={(event) => onEndMonthChange?.(event.target.value)}
          />
        </Flex>
        <Flex align="center" gap={1}>
          <Checkbox
            isChecked={extendFundingEnabled(localSettings)}
            onChange={(event) => setLocalSettings((current) => ({
              ...(current || {}),
              extendFunding: event.target.checked,
            }))}
          >
            Extend funding
          </Checkbox>
          <Hint label={extendFundingHint} />
        </Flex>
        <Flex align="center" gap={3} ml="auto">
          <Button size="sm" variant="outline" onClick={openDefaultMarketing}>
            Default marketing
          </Button>
          <Button size="sm" variant="outline" onClick={openGeneric}>
            Generic curve
          </Button>
          {!readOnly && (
            <Button
              size="sm"
              colorScheme="brand"
              isLoading={recalculating}
              onClick={recalculate}
            >
              Save & Run
            </Button>
          )}
        </Flex>
      </Flex>
      {formatLastRun(settings, userName) && (
        <Text fontSize="xs" color="gray.500" mb={2}>{formatLastRun(settings, userName)}</Text>
      )}
      <Text fontSize="sm" color="gray.600" mb={3}>
        From and Through are the months shown. Check Extend funding and press Save & Run to fund through the Through month.
      </Text>
      <MarketingFillExplanation settings={localSettings} />
      </Box>
      )}
      <Text fontSize="sm" fontWeight={700} letterSpacing="0.06em" mb={3}>
        LIVE CUSTOMERS
      </Text>
      <Box overflowX="auto" borderWidth="1px" borderColor="gray.100" borderRadius="12px" mb={8}>
        <Table size="sm" variant="simple">
          <Thead>
            <Tr>
              <Th {...headerCell}>Customer</Th>
              <Th {...headerCell}>Commitment</Th>
              <Th {...headerCell}>First installment started</Th>
              <Th {...headerCell}>
                <Flex align="center">
                  Last installment
                  <Hint label={lastInstallmentHint} />
                </Flex>
              </Th>
              <Th {...headerCell}>
                <Flex align="center">
                  Marketing growth %
                  <Hint label={liveMarketingGrowthHint} />
                </Flex>
              </Th>
            </Tr>
          </Thead>
          <Tbody>
            {liveRows.length === 0 && (
              <Tr>
                <Td colSpan={5}>
                  <Text fontSize="sm" color="gray.500" py={4}>No live customers.</Text>
                </Td>
              </Tr>
            )}
            {liveRows.map((client) => {
              const commitment = Number(client.commitmentLength) || 0;
              const savedMonths = client.savedMarketingMonths;
              const missingMarketing = commitment > 0 && Array.isArray(savedMonths) && savedMonths.length === 0;
              return (
                <Tr key={client.customerId}>
                  <Td px={2} minW="160px">
                    <Text fontSize="sm" fontWeight={600} color={missingMarketing ? 'red.600' : undefined}>
                      {client.companyName}
                    </Text>
                    {missingMarketing && (
                      <Text fontSize="xs" color="red.600" mt={1}>
                        No marketing spend is set. Update it on the{' '}
                        <Link
                          as={RouterLink}
                          to={`/performance/marketing?customerId=${client.customerId}`}
                          color="red.600"
                        >
                          marketing screen
                        </Link>
                        {hasDefaultMarketing(localSettings)
                          ? '. Its fundings use the default marketing until real marketing is loaded.'
                          : '. Its fundings are zero until real marketing is loaded or Default marketing is set.'}
                      </Text>
                    )}
                  </Td>
                  <Td px={2} fontSize="sm">{commitment > 0 ? `${commitment} months` : '—'}</Td>
                  <Td px={2} fontSize="sm">{formatInstallmentDate(client.firstInstallmentStarted) || '—'}</Td>
                  <Td px={2} fontSize="sm">
                    {formatInstallmentDate(client.lastInstallment) || '—'}
                    {client.lastInstallmentFrom === 'commitment_date' && (
                      <Text fontSize="xs" color="gray.500">
                        {`Not funded yet. First funding on the commitment date, ${formatInstallmentDate(client.commitmentDate)}, then ${Math.max(0, commitment - 1)} more.`}
                      </Text>
                    )}
                    {client.lastInstallmentFrom === 'last_funding' && (
                      <Text fontSize="xs" color="gray.500">
                        {`${client.fundedCohorts} of ${commitment} cohorts funded, last in ${formatInstallmentDate(client.lastFunding)}; ${Math.max(0, commitment - client.fundedCohorts)} left.`}
                      </Text>
                    )}
                  </Td>
                  <Td px={2}>
                    <DraftNumber
                      width="72px"
                      precision={1}
                      ariaLabel={`Marketing growth for ${client.companyName || 'live customer'}`}
                      value={ownLiveMarketingGrowth(client.marketingGrowth) == null ? '' : liveMarketingGrowthPercent(client.marketingGrowth)}
                      placeholder={`${fillGrowthPercent(localSettings)}`}
                      onCommit={(next) => {
                        const percent = next === '' ? null : Number(next);
                        const fraction = percent === null || !Number.isFinite(percent) || percent < 0 || percent > 100 ? null : percent / 100;
                        patchLiveRow(client.customerId, { marketingGrowth: fraction });
                      }}
                    />
                  </Td>
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      </Box>
      <Flex align="center" gap={3} mb={3} wrap="wrap">
        <Text fontSize="sm" fontWeight={700} letterSpacing="0.06em">
          {`NON LIVE CUSTOMERS (${customers.length})`}
        </Text>
        <Button
          size="xs"
          variant="outline"
          aria-expanded={nonLiveOpen}
          onClick={() => {
            storeNonLiveOpen(!nonLiveOpen);
            setNonLiveOpen(!nonLiveOpen);
          }}
        >
          {nonLiveOpen ? 'Hide customers' : 'Show customers'}
        </Button>
      </Flex>
      {renderGapSummary(customers, false)}
      {nonLiveOpen ? renderTable(customers, false) : renderUnfundedWarnings(customers)}

      <Flex justify="space-between" align="center" mt={8} mb={3} wrap="wrap" gap={3}>
        <Text fontSize="sm" fontWeight={700} letterSpacing="0.06em">
          FUTURE CUSTOMERS
        </Text>
        <Button size="sm" variant="outline" onClick={addFutureCustomer}>
          Add
        </Button>
      </Flex>
      {renderGapSummary(futureCustomers, true)}
      {renderTable(futureCustomers, true)}

      <Box mt={8}>
        <Text fontSize="sm" fontWeight={700} mb={2}>
          How funding and repayments work
        </Text>
        <Text fontSize="sm" color="gray.600" mb={2}>
          For a non-live or future customer, the last step of the repayment vector repeats until that cohort is paid off, including unpaid fees. The step is the change from the previous number. A vector of 10, 20, 30 repeats 10% of the funding each month. A vector that ends flat, such as 10, 20, 30, 30, repeats 0%, so nothing more is collected.
        </Text>
        <Text fontSize="sm" color="gray.600" mb={2}>
          A live customer has a fixed number of repayments. Months after the vector reuse the last value inside that schedule, and the last repayment collects the remaining principal. When the cohort has a revenue row, that last month&apos;s revenue is reused. When it is still on the ROAS curve, the last incremental ROAS is reused.
        </Text>
        <Text fontSize="sm" color="gray.600">
          A live customer keeps the marketing already stored. Months after the last saved month grow by that customer's marketing percent, including when Extend funding is on. The default is 10%, so 1,000,000 becomes 1,100,000 and then 1,210,000. Non-live customers checked as Default Marketing use the shared first spend and monthly growth. The marketing spend saved for them is ignored. A non-live name in red does not have enough saved marketing to cover the commitment. Check Extend funding and press Save & Run to fund through the end date. A non-live or future customer then grows by 10%.
        </Text>
      </Box>

      <Modal isOpen={defaultMarketingOpen} onClose={() => setDefaultMarketingOpen(false)} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Default marketing</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text fontSize="sm" color="gray.600" mb={3}>
              Used for every customer, live or not, for funded months with no saved marketing before them: the first month uses the first spend, and each following month grows by the monthly percent. The monthly percent also grows every customer past its last saved marketing month (live customers use their own Marketing growth % when it is filled in). Saved marketing is never replaced.
            </Text>
            <Text fontSize="xs" color="gray.500" mb={1}>First marketing spend</Text>
            <GroupedAmountInput
              width="220px"
              aria-label="First marketing spend"
              value={defaultSpendDraft}
              onChange={setDefaultSpendDraft}
            />
            <Text fontSize="xs" color="gray.500" mt={4} mb={1}>Monthly growth %</Text>
            <NumberInput
              size="sm"
              w="120px"
              value={defaultGrowthDraft}
              precision={1}
              onChange={setDefaultGrowthDraft}
            >
              <NumberInputField aria-label="Monthly marketing growth" px={2} />
            </NumberInput>
          </ModalBody>
          <ModalFooter>
            <Button size="sm" colorScheme="brand" onClick={commitDefaultMarketing}>Done</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={genericOpen} onClose={() => setGenericOpen(false)} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Generic curve</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text fontSize="sm" color="gray.600" mb={3}>
              Customers checked as Generic use this curve instead of their own repayment vector.
            </Text>
            <CurvePasteField value={genericText} onChange={setGenericText} />
          </ModalBody>
          <ModalFooter>
            <Button size="sm" colorScheme="brand" onClick={commitGeneric}>Done</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={!!realMarketingClient} onClose={() => setRealMarketingClient(null)} size="4xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{`Marketing — ${realMarketingClient?.companyName || ''}`}</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <Text fontSize="sm" color="gray.600" mb={4}>
              This is the customer&apos;s real marketing, the same as Manual Marketing Spend on the marketing page. Save
              stores it right away for every screen that uses this customer&apos;s marketing, not only Sources &amp; Uses,
              and Leave without saving doesn&apos;t undo it. Press Save &amp; Run afterwards to use it here.
            </Text>
            {realMarketingClient && (
              <MarketingSpendSection
                customerId={realMarketingClient.customerId}
                embedded
                onSaved={() => toast({
                  title: 'Press Save & Run to use the new marketing',
                  status: 'info',
                  duration: 6000,
                })}
              />
            )}
          </ModalBody>
        </ModalContent>
      </Modal>

      <Modal isOpen={!!vectorClient} onClose={() => setVectorClientId(null)} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Repayment vector{vectorClient ? ` — ${vectorClient.companyName || 'Future customer'}` : ''}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <CurvePasteField value={vectorText} onChange={setVectorText} />
          </ModalBody>
          <ModalFooter>
            <Button size="sm" colorScheme="brand" onClick={commitVector}>Done</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={!!marketingClient} onClose={() => setMarketingClientId(null)} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Marketing{marketingClient ? ` — ${marketingClient.companyName || 'Future customer'}` : ''}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text fontSize="sm" color="gray.600" mb={3}>
              Enter the marketing spend for each month. Funding in a month uses the prior month.
            </Text>
            {marketingRows.map((row, index) => (
              <Flex key={`${row.month}-${index}`} gap={3} mb={2} align="center">
                <Input
                  type="month"
                  size="sm"
                  w="160px"
                  aria-label={`Marketing month ${index + 1}`}
                  value={row.month || ''}
                  onChange={(event) => setMarketingRows((current) => current.map((item, itemIndex) => (
                    itemIndex === index ? { ...item, month: event.target.value } : item
                  )))}
                />
                <GroupedAmountInput
                  width="180px"
                  aria-label={`Marketing spend ${index + 1}`}
                  value={row.spend ?? ''}
                  onChange={(next) => setMarketingRows((current) => current.map((item, itemIndex) => (
                    itemIndex === index ? { ...item, spend: next } : item
                  )))}
                />
                <Button
                  size="xs"
                  variant="ghost"
                  onClick={() => setMarketingRows((current) => current.filter((_, itemIndex) => itemIndex !== index))}
                >
                  Remove
                </Button>
              </Flex>
            ))}
            <Button
              size="sm"
              variant="outline"
              mt={2}
              onClick={() => setMarketingRows((current) => [...current, { month: '', spend: 0 }])}
            >
              Add month
            </Button>
          </ModalBody>
          <ModalFooter>
            <Button size="sm" colorScheme="brand" onClick={commitMarketing}>Done</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}

PipelineClientsTable.propTypes = {
  clients: PropTypes.arrayOf(PropTypes.object),
  liveCustomers: PropTypes.arrayOf(PropTypes.object),
  settings: PropTypes.shape({
    spendPerMonth: PropTypes.number,
    startMonth: PropTypes.string,
    genericVector: PropTypes.arrayOf(PropTypes.object),
    defaultMarketingSpend: PropTypes.number,
    defaultMarketingGrowth: PropTypes.number,
    lastFundingMonth: PropTypes.string,
    extendFunding: PropTypes.bool,
    lastRunAt: PropTypes.string,
    lastRunBy: PropTypes.string,
  }),
  tenant: PropTypes.string,
  fundingEndMonth: PropTypes.string,
  startMonth: PropTypes.string,
  endMonth: PropTypes.string,
  onStartMonthChange: PropTypes.func,
  onEndMonthChange: PropTypes.func,
  showToolbar: PropTypes.bool,
  stages: PropTypes.arrayOf(PropTypes.string),
  onRecalculate: PropTypes.func.isRequired,
  recalculating: PropTypes.bool,
  readOnly: PropTypes.bool,
  onDirtyChange: PropTypes.func,
  runRef: PropTypes.shape({ current: PropTypes.any }),
};

export default PipelineClientsTable;
